import React, { useState, useEffect, useRef } from "react";
import apiService from "../services/apiService";
import { useNavigate, useSearchParams } from "react-router-dom";
import { formatDifficulty } from "../utils/formatters";
import AIDisclaimer from "../components/AIDisclaimer";
import MathText from "../components/MathText";
import './css/Quiz.css';

function Quiz() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const courseIdFromUrl = searchParams.get('courseId');
  const lessonIdFromUrl = searchParams.get('lessonId');
  const [selectedCourse, setSelectedCourse] = useState(courseIdFromUrl || "");
  const [selectedCourseName, setSelectedCourseName] = useState("");
  const [quizName, setQuizName] = useState("");
  const [selectedLesson, setSelectedLesson] = useState(lessonIdFromUrl || "");
  const [selectedLessonName, setSelectedLessonName] = useState("");
  const [difficulty, setDifficulty] = useState("easy");
  const [quizMode, setQuizMode] = useState('basic');
  const [documents, setDocuments] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [advancedSpecs, setAdvancedSpecs] = useState([{ n: 1, blooms_level: 'remember', difficulty: 'easy', outcomes: [] }]);
  const [questionType, setQuestionType] = useState('multiple_choice');
  const [numQuestions, setNumQuestions] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [lessonHasDocSource, setLessonHasDocSource] = useState(true);
  const [docSourceWarning, setDocSourceWarning] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [courseOutcomes, setCourseOutcomes] = useState([]);
  const [pollingInfo, setPollingInfo] = useState(null);
  const [generationProgress, setGenerationProgress] = useState("");
  
  const pollingIntervalRef = useRef(null);

  useEffect(() => {
    const fetchCourseAndLesson = async () => {
      if (!courseIdFromUrl || !lessonIdFromUrl) {
        setError("Course ID and Lesson ID are required in URL parameters.");
        return;
      }

      try {
        // Fetch course name
        const courseData = await apiService.getCourseById(courseIdFromUrl);
        setSelectedCourseName(courseData.title);
        setSelectedCourse(courseIdFromUrl);

        // Fetch lesson name
        const lessonData = await apiService.getLessonById(lessonIdFromUrl);
        setSelectedLessonName(lessonData.title);
        setSelectedLesson(lessonIdFromUrl);
      } catch (err) {
        console.error("Error fetching course/lesson:", err);
        setError("Failed to load course or lesson information.");
      }
    };
    fetchCourseAndLesson();
  }, [courseIdFromUrl, lessonIdFromUrl]);

  // Polling effect for question generation status
  useEffect(() => {
    if (pollingInfo) {
      console.log("Starting question generation polling:", pollingInfo);
      setGenerationProgress("Question generation in progress, please wait...");
      
      const pollStatus = async () => {
        try {
          console.log("Checking question generation status...");
          
          const response = await apiService.checkQuestionGenerationStatus(
            pollingInfo.lessonId,
            pollingInfo.questionSetId
          );

          if (response.success) {
            const { status, question_set, questions, count, error: statusError } = response.data;

            if (status === 'completed') {
              console.log("✅ Question generation complete!");
              
              // Clear polling
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }
              setPollingInfo(null);
              setLoading(false);
              setGenerationProgress("");

              // Set result and show modal
              setResult({
                question_set: {
                  id: question_set?.id || pollingInfo.questionSetId,
                  name: question_set?.name || pollingInfo.quizName || 'Generated Question Set',
                  answer_style: pollingInfo.questionType,
                  question_count: count || questions?.length || 0,
                  created_at: question_set?.created_at || new Date().toISOString()
                },
                questions: questions || [],
                showDisclaimer: true
              });
              setShowModal(true);

            } else if (status === 'failed') {
              console.error("❌ Question generation failed:", statusError);
              
              // Clear polling
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }
              setPollingInfo(null);
              setLoading(false);
              setGenerationProgress("");
              setError(`Question generation failed: ${statusError || 'Unknown error'}`);

            } else if (status === 'processing') {
              // Still processing, continue polling
              console.log("⏳ Still processing...");
              setGenerationProgress("Question generation in progress, please wait...");
            }
          } else {
            console.error("Failed to check generation status:", response.error);
            // Don't stop polling on temporary errors
          }
        } catch (err) {
          console.error("Error polling for generation status:", err);
          // Don't stop polling on temporary errors
        }
      };

      // Start polling immediately
      pollStatus();
      
      // Then poll every 3 seconds (as per backend recommendation)
      pollingIntervalRef.current = setInterval(pollStatus, 5000);

      // Cleanup function
      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    }
  }, [pollingInfo]);

  const handleCreateQuiz = async (e) => {
    e.preventDefault();
    
    // Validate lesson is selected
    if (!selectedLesson) {
      setError("Please select a lesson before generating the question set.");
      return;
    }
    
    setLoading(true);
    setError(null);
    setResult(null);
    setGenerationProgress("Starting question generation...");
    
    try {
      let payload;
      
      if (quizMode === 'basic') {
        // Simple mode - removed blooms_level
        payload = {
          mode: 'simple',
          question_type: questionType,
          n: parseInt(numQuestions, 10),
          difficulty: difficulty,
          question_set_name: quizName || undefined,
          document_ids: selectedDocuments.length > 0 ? selectedDocuments.map(id => parseInt(id, 10)) : undefined
        };
      } else {
        // Advanced mode
        // Document IDs are optional - if not provided, backend will use lesson's transcript
        payload = {
          mode: 'advanced',
          question_type: questionType,
          question_set_name: quizName || undefined,
          specs: advancedSpecs.map(spec => ({
            n: parseInt(spec.n, 10),
            blooms_level: spec.blooms_level,
            difficulty: spec.difficulty,
            outcomes: spec.outcomes && spec.outcomes.length > 0 ? spec.outcomes : undefined
          })),
          question_set_name: quizName || undefined,
          document_ids: selectedDocuments.length > 0 ? selectedDocuments.map(id => parseInt(id, 10)) : undefined
        };
      }
      
      console.log("Sending payload:", payload);
      
      // Use the unified function for both modes
      const response = await apiService.startQuestionGeneration(selectedLesson, payload);
      
      if (response.success) {
        // Start polling for status
        setPollingInfo({
          lessonId: selectedLesson,
          questionSetId: response.data.question_set_id,
          questionType: questionType,
          quizName: quizName
        });
      } else {
        throw new Error(response.error || 'Failed to start question generation');
      }
      
    } catch (err) {
      setError(err.message || 'Failed to generate question set.');
      setLoading(false);
      setGenerationProgress("");
    }
  };

  useEffect(() => {
    const fetchCourseOutcomes = async () => {
      if (!courseIdFromUrl) return;
      
      try {
        const courseData = await apiService.getCourseById(courseIdFromUrl);
        setCourseOutcomes(courseData.outcomes || []);
      } catch (err) {
        console.error("Error fetching course outcomes:", err);
      }
    };
    fetchCourseOutcomes();
  }, [courseIdFromUrl]);

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!selectedLesson) {
        setDocuments([]);
        setSelectedDocuments([]);
        return;
      }
      try {
        const lessonData = await apiService.getLessonById(selectedLesson);
        // Only include PDF and text resources for question generation
        const sourceDocuments = lessonData.doc_resources?.filter(
          doc => doc.category === 'lesson' && (doc.filetype === 'pdf' || doc.filetype === 'text')
        ) || [];
        setDocuments(sourceDocuments);
        // Automatically select all documents
        setSelectedDocuments(sourceDocuments.map(doc => String(doc.id)));
      } catch (err) {
        console.error("Error fetching documents for lesson:", err);
        setError("Failed to load documents for the selected lesson.");
      }
    };

    fetchDocuments();
  }, [selectedLesson]);

  const handleQuestionTypeChange = (e) => {
    const newType = e.target.value;
    setQuestionType(newType);
  };

  const handleSpecChange = (index, field, value) => {
    const newSpecs = [...advancedSpecs];
    newSpecs[index][field] = value;
    setAdvancedSpecs(newSpecs);
  };

  const addSpec = () => {
    setAdvancedSpecs([...advancedSpecs, { n: 1, blooms_level: 'remember', difficulty: 'easy', outcomes: [] }]);
  };

  const removeSpec = (index) => {
    setAdvancedSpecs(advancedSpecs.filter((_, i) => i !== index));
  };

  const convertToCSV = (quizData) => {
    if (!quizData || !quizData.questions || quizData.questions.length === 0) {
      return '';
    }

    const questions = quizData.questions;
    const isMultipleChoice = quizData.question_set?.answer_style === 'multiple_choice';

    let maxOptions = 0;
    if (isMultipleChoice) {
      questions.forEach(q => {
        if (q.options && q.options.length > maxOptions) {
          maxOptions = q.options.length;
        }
      });
    }

    const baseHeaders = [
      'question_number',
      'text',
      'type',
      'blooms_taxonomy',
      'difficulty',
      'outcomes'
    ];

    const optionHeaders = [];
    if (isMultipleChoice && maxOptions > 0) {
      for (let i = 0; i < maxOptions; i++) {
        optionHeaders.push(`option_${i + 1}`);
      }
      optionHeaders.push('correct_option');
    }

    const headers = [...baseHeaders, ...optionHeaders];

    const csvContent = [
      headers.map(header => `"${header}"`).join(','),
      ...questions.map(question => {
        const row = [];
        baseHeaders.forEach(header => {
          const value = question[header];
          if (value === null || value === undefined) {
            row.push('""');
          } else {
            row.push(`"${String(value).replace(/"/g, '""')}"`);
          }
        });

        if (isMultipleChoice && maxOptions > 0) {
          for (let i = 0; i < maxOptions; i++) {
            if (question.options && question.options[i]) {
              const option = question.options[i];
              row.push(`"${String(option.text || '').replace(/"/g, '""')}"`);
            } else {
              row.push('""');
            }
          }

          let correctOptionNumber = '';
          if (question.options) {
            const correctIndex = question.options.findIndex(opt => opt.is_correct === true);
            if (correctIndex !== -1) {
              correctOptionNumber = correctIndex + 1;
            }
          }
          row.push(`"${correctOptionNumber}"`);
        }

        return row.join(',');
      })
    ].join('\n');

    return csvContent;
  };

  const handleDownload = () => {
    if (!result) return;

    const quizData = {
      question_set: result.question_set,
      quiz_name: quizName || result.question_set?.name,
      created_at: result.question_set?.created_at,
      answer_style: result.question_set?.answer_style,
      question_count: result.question_set?.question_count,
      questions: result.questions.map((q, index) => {
        const questionData = {
          question_number: index + 1,
          text: q.text,
          type: q.type,
          blooms_taxonomy: q.blooms_taxonomy,
          difficulty: formatDifficulty(q.difficulty),
          outcomes: q.outcomes?.map(o => o.short_name).join(', ') || '',
        };

        if (q.type === 'multiple_choice' && q.options) {
          questionData.options = q.options.map(opt => ({
            text: opt.text,
            is_correct: opt.is_correct,
          }));
        }
        return questionData;
      })
    };

    const csvData = convertToCSV(quizData);
    const dataUri = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvData);

    const exportFileDefaultName = `quiz_${quizName || 'generated'}_${new Date().toISOString().split('T')[0]}.csv`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const handleCancel = () => {
    setShowModal(false);
    setResult(null);
  };

  if (!courseIdFromUrl || !lessonIdFromUrl) {
    return (
      <div className="upload-container">
        <h1>Create New Question Set</h1>
        <div className="upload-error">
          Course ID and Lesson ID are required. Please navigate from a lesson page.
        </div>
      </div>
    );
  }

  return (
    <div>
      <button 
        className="btn btn-secondary" 
        onClick={() => navigate(`/lessons/${lessonIdFromUrl}/resources`)}
      >
        ← Back to Lesson
      </button>
      <div className="upload-container" style={{ marginTop: "1rem" }}>
        <h1>Create New Question Set</h1>
        
        {selectedCourseName && (
          <p style={{ color: "var(--text-light)", marginTop: "0.5rem", marginBottom: "1rem" }}>
            Course: <strong>{selectedCourseName}</strong>
            {selectedLessonName && (
              <> | Lesson: <strong>{selectedLessonName}</strong></>
            )}
          </p>
        )}
      <div className="form-group">
        <label>Question Set Name</label>
        <input className="form-control" value={quizName} onChange={e => setQuizName(e.target.value)} />
      </div>

      <div className="form-group">
        <label>Question Type</label>
        <select
          className="form-control"
          value={questionType}
          onChange={handleQuestionTypeChange}
        >
          <option value="multiple_choice">Multiple Choice</option>
          <option value="essay">Essay</option>
          <option value="problems">Problems (Advanced Mode Only)</option>
        </select>
      </div>

      <div className="form-group">
        <label>Question Set Mode</label>
        <div className="btn-group">
          <button
            className={`btn ${quizMode === 'basic' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setQuizMode('basic')}
          >
            Basic (Simple)
          </button>
          <button
            className={`btn ${quizMode === 'advanced' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setQuizMode('advanced')}
          >
            Advanced
          </button>
        </div>
      </div>

      {quizMode === 'basic' ? (
        <div id="basic-mode-container" className="basic-mode-container">
          <div className="form-group">
            <label>Number of Questions</label>
            <input className="form-control" type="number" value={numQuestions} onChange={e => setNumQuestions(e.target.value)} min={1} max={50} />
          </div>

          <div className="form-group">
            <label>Difficulty</label>
            <select
              className="form-control"
              value={difficulty}
              onChange={e => setDifficulty(e.target.value)}
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="difficult">Hard</option>
            </select>
          </div>
        </div>
      ) : (
        <div id="advanced-mode-container" className="advanced-mode-container">
          <>
            <h4>Question Specifications</h4>
            <br />
            {advancedSpecs.map((spec, index) => (
              <div key={index} className="spec-card">
                <div className="spec-header">
                  <h5>Specification #{index + 1}</h5>
                  <button className="btn btn-danger btn-sm" onClick={() => removeSpec(index)}>Remove</button>
                </div>
                <div className="spec-inputs">
                  <div className="form-group">
                    <label>Number of Questions</label>
                    <input type="number" className="form-control" value={spec.n} onChange={e => handleSpecChange(index, 'n', e.target.value)} min="1" />
                  </div>
                  <div className="form-group">
                    <label>Bloom's Level</label>
                    <select className="form-control" value={spec.blooms_level} onChange={e => handleSpecChange(index, 'blooms_level', e.target.value)}>
                      <option value="remember">Remember</option>
                      <option value="understand">Understand</option>
                      <option value="apply">Apply</option>
                      <option value="analyze">Analyze</option>
                      <option value="evaluate">Evaluate</option>
                      <option value="create">Create</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Difficulty</label>
                    <select className="form-control" value={spec.difficulty} onChange={e => handleSpecChange(index, 'difficulty', e.target.value)}>
                      <option value="easy">Easy</option>
                      <option value="medium">Medium</option>
                      <option value="difficult">Hard</option>
                    </select>
                  </div>
                  <div className="form-group outcome-group">
                    <label>Learning Outcomes (Optional - Multi-select)</label>
                    <select
                      className="form-control"
                      multiple
                      value={spec.outcomes || []}
                      onChange={e => {
                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                        handleSpecChange(index, 'outcomes', selected);
                      }}
                      style={{ height: '120px' }}
                    >
                      {courseOutcomes.map(outcome => (
                        <option key={outcome.short_name} value={outcome.short_name}>
                          {outcome.short_name}: {outcome.description}
                        </option>
                      ))}
                    </select>
                    {courseOutcomes.length === 0 && (
                      <small className="form-text text-muted">
                        No outcomes defined for this course.
                      </small>
                    )}
                    {spec.outcomes && spec.outcomes.length > 0 && (
                      <small className="form-text text-muted">
                        Selected: {spec.outcomes.join(', ')}
                      </small>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <button className="btn btn-secondary" onClick={addSpec}>+ Add Specification</button>
            <br/>
          </>
        </div>
      )}

      <button 
        className="btn btn-primary generate-quiz-btn" 
        onClick={handleCreateQuiz} 
        disabled={loading || !selectedLesson}
      >
        {loading ? 'Generating...' : '✨ Generate Question Set'}
      </button>
      
      {generationProgress && (
        <div className="alert alert-info" style={{ marginTop: '1rem' }}>
          {generationProgress}
        </div>
      )}
      
      {docSourceWarning && <div className="alert alert-warning doc-source-warning">{docSourceWarning}</div>}
      {error && <div className="alert alert-danger">{error}</div>}
      </div>

      {showModal && result && (
        <div className="modal-overlay" onClick={handleCancel}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4 className="modal-title">Generated Question Set</h4>
              <button
                type="button"
                className="close modal-close-btn"
                onClick={handleCancel}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="quiz-info">
                <h3>{result.question_set?.name}</h3>
                <p><strong>Answer Style:</strong> {result.question_set?.answer_style}</p>
                <p><strong>Total Questions:</strong> {result.question_set?.question_count}</p>
                <p><strong>Created:</strong> {new Date(result.question_set?.created_at).toLocaleString()}</p>
              </div>
              <hr />
              <AIDisclaimer message="These questions were generated by AI. Please review and verify the accuracy before using them." />
              {result.questions.map((question, index) => (
                <div key={question.id} className="question-card">
                  <div className="question-header">
                    <span className="badge badge-question">
                      Q{index + 1}
                    </span>
                    <span className="badge badge-blooms">
                      Bloom's: {question.blooms_taxonomy || 'N/A'}
                    </span>
                    <span className="badge badge-difficulty">
                      Difficulty: {formatDifficulty(question.difficulty)}
                    </span>
                    {question.outcomes && question.outcomes.length > 0 && (
                      <span className="badge badge-outcome">
                        Outcomes: {question.outcomes.map(o => o.short_name).join(', ')}
                      </span>
                    )}
                  </div>
                  <div className="question-text">
                    <strong>Question:</strong>
                    <MathText>{question.text}</MathText>
                  </div>
                  {question.type === 'multiple_choice' && question.options && (
                    <div className="question-options">
                      <strong>Options:</strong>
                      <ul>
                        {question.options.map((option, optIndex) => (
                          <li key={option.id} className={option.is_correct ? 'option-correct' : ''}>
                            <MathText>{`${optIndex + 1}) ${option.text}`}</MathText>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-info" onClick={handleDownload}>
                📥 Download
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                ❌ Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Quiz;